import { useTranslation } from '@/i18n/useTranslation';

import { useState } from 'react';
import { ApiError } from '@/api/client';
import { downloadSavedInspections, saveBlob } from '@/api/endpoints/exports';
import { useAuth } from '@/auth/useAuth';
import { InferenceExtractionPanel } from '@/components/admin/InferenceExtractionPanel';

const formats = [
  { value: 'csv', label: 'CSV (.csv)', download: 'Download CSV' },
  { value: 'xlsx', label: 'Excel (.xlsx)', download: 'Download Excel' },
  { value: 'docx', label: 'Word (.docx)', download: 'Download Word' },
  { value: 'pdf', label: 'PDF (.pdf)', download: 'Download PDF' },
] as const;

export function ExportCenterPage() {
  const { errorText, t } = useTranslation();
  const { role } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [format, setFormat] = useState<(typeof formats)[number]>(formats[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const invalidRange = Boolean(from && to && from > to);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await downloadSavedInspections(format.value, {
        date_from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
        date_to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
      });
      saveBlob(blob, `saved-inspections.${format.value}`);
    } catch (cause) {
      setError({ cause });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <header>
        <h1>{t('Export Center')}</h1>
        <p>
          {t(
            'Download one row per prediction with the owner username, label and confidence. Inspections with no detections are included.',
          )}
        </p>
      </header>
      <section className="glass-panel stack">
        <h2>{t('Saved inspection exports')}</h2>
        <div className="cluster">
          <label className="field">
            {t('From')}
            <input
              type="date"
              className="input"
              value={from}
              disabled={busy}
              onChange={(event) => setFrom(event.target.value)}
            />
          </label>
          <label className="field">
            {t('To')}
            <input
              type="date"
              className="input"
              value={to}
              disabled={busy}
              onChange={(event) => setTo(event.target.value)}
            />
          </label>
          <label className="field">
            {t('Export format')}
            <select
              className="input"
              value={format.value}
              disabled={busy}
              onChange={(event) => {
                const selected = formats.find((option) => option.value === event.target.value);
                if (!selected) throw new Error('Unsupported export format');
                setFormat(selected);
              }}
            >
              {formats.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p>
          {t(
            'Excel contains the same columns as CSV. Word contains an editable table. PDF includes the table and the Activity over time chart for this date range.',
          )}
        </p>
        {invalidRange && (
          <p role="alert">{t('The start date must not be later than the end date.')}</p>
        )}
        <div>
          <button
            type="button"
            className="btn btn--accent"
            disabled={busy || invalidRange}
            onClick={() => void download()}
          >
            {busy ? t('Preparing…') : t(format.download)}
          </button>
        </div>
        {error && (
          <p role="alert" className="error-state">
            {error.cause instanceof Error
              ? errorText(
                  error.cause.message,
                  error.cause instanceof ApiError ? error.cause.status : undefined,
                )
              : t('Could not export inspections.')}
          </p>
        )}
      </section>
      {(role === 'admin' || role === 'super_admin') && <InferenceExtractionPanel />}
    </div>
  );
}
